import React from 'react';

import { withErrorBoundary } from '@justgiving/error-boundary';
import loadable from '@loadable/component';

import clientConfig from '../../client/config';
import { usePageMeta, usePageOwner } from '../../hooks';
import { useAuthenticateUser } from '../../hooks/useAuthenticateUser';
import componentDidCatch from '../../utils/componentDidCatch';
import ErrorPageWrapper from '../ErrorPage/ErrorPageWrapper';

const TeamsPage = loadable(() =>
  import(/* webpackChunkName: "TeamsPage" */ '../TeamsPage/TeamsPage'),
);
const CampaignPage = loadable(() =>
  import(/* webpackChunkName: "CampaignPage" */ '../CampaignPage/CampaignPage'),
);
const FundraisingPage = loadable(() =>
  import(
    /* webpackChunkName: "FundraisingPage" */ '../FundraisingPage/FundraisingPage'
  ),
);
const InMemoryPage = loadable(() =>
  import(/* webpackChunkName: "InMemoryPage" */ '../InMemoryPage/InMemoryPage'),
);
const OnePage = loadable(() =>
  import(/* webpackChunkName: "OnePage" */ '../OnePage/OnePage'),
);
const SiteBuilderFRP = loadable(() =>
  import(
    /* webpackChunkName: "SiteBuilderFRP" */ '../SiteBuilderFRP/SiteBuilderFRP'
  ),
);
const SiteBuilderTeamsPage = loadable(() =>
  import(
    /* webpackChunkName: "SiteBuilderTeamsPage" */ '../SiteBuilderTeamsPage/SiteBuilderTeamsPage'
  ),
);

export function PageType() {
  const {
    isPreview,
    currentUrl,
    pageType,
    error,
    basePageData,
  } = usePageMeta();
  const { isUserLoggedIn } = useAuthenticateUser();
  const { isCheckingPageOwner, data } = usePageOwner();

  // TODO - refactor this to use Tags when we got tags synced with JGPro
  const campaignPageRelationships =
    basePageData?.relationships?.campaigns.nodes[0];
  const isSitebuilder = campaignPageRelationships?.isSitebuilder;

  if (isPreview && !isUserLoggedIn) {
    window.location.assign(`${clientConfig.loginUrl}${encodeURI(currentUrl)}`);
    return null;
  }

  if (isPreview && !isCheckingPageOwner && !data?.legacyId) {
    return <ErrorPageWrapper status={404} messageKey={'page.error404'} />;
  }

  if (error) {
    return (
      <ErrorPageWrapper status={error.status} messageKey={error.messageKey} />
    );
  }

  if (isPreview && isCheckingPageOwner) {
    return null;
  }

  if (pageType === 'team') {
    return !isSitebuilder ? <TeamsPage /> : <SiteBuilderTeamsPage />;
  }

  if (pageType === 'campaign') {
    return <CampaignPage />;
  }

  if (pageType === 'fundraising') {
    return !isSitebuilder ? <FundraisingPage /> : <SiteBuilderFRP />;
  }

  if (pageType === 'one_page') {
    return <OnePage />;
  }

  if (pageType === 'in_memory') {
    return <InMemoryPage />;
  }

  return null;
}

export default withErrorBoundary(
  PageType,
  <ErrorPageWrapper />,
  componentDidCatch,
);
